import { useCallback, useMemo, useState } from "react";

import { Col, Row, Typography } from "antd";
import useBreakpoint from "antd/es/grid/hooks/useBreakpoint";
import { cn } from "lib/utils";

import { useGetContracts } from "hooks/useContracts";

import ContractFilters from "js/components/Contracts/ContractFilters";
import ContractsTable from "js/components/Contracts/ContractsTable/ContractsTable";
import TableSearch from "js/components/TableSearch";
import { PaginationTable } from "js/components/types";
import { Button } from "js/components/ui/button";

import CreateLeaseContractModal from "./CreateLeaseContractModal";

const { Title } = Typography;

const HEADER_ROW_STYLE: React.CSSProperties = {
  marginBottom: 15,
  marginTop: 24,
};

type Filters = {
  status?: string;
  is_active?: string;
};

/** Row with search, optional filters, and a primary button */
function ControlsRow(props: {
  isLg: boolean;
  searchLabel: string;
  onSearch: (v: string) => void;
  onClear: () => void;
  filters?: React.ReactNode;
  rightButton: React.ReactNode;
  className?: string;
}) {
  const {
    isLg,
    searchLabel,
    onSearch,
    onClear,
    filters,
    rightButton,
    className,
  } = props;
  return (
    <div
      className={cn(
        "flex flex-col gap-3",
        isLg ? "md:flex-row md:items-center" : "",
        className,
      )}
    >
      <div className="w-full min-w-[240px] md:max-w-md">
        <TableSearch
          screen={{ lg: isLg } as any}
          onSearch={onSearch}
          onClear={onClear}
          label={searchLabel}
        />
      </div>
      {filters && (
        <div className="flex w-full flex-1 flex-wrap gap-2 md:justify-start">
          {filters}
        </div>
      )}
      <div className="flex w-full justify-end md:w-auto md:pl-4">
        {rightButton}
      </div>
    </div>
  );
}

const ContractsDashboard = () => {
  const screen = useBreakpoint();
  const [contractSearch, setContractSearch] = useState("");
  const [showCreateContractModal, setShowCreateContractModal] = useState(false);
  const [contractFilters, setContractFilters] = useState<Filters>({});
  const [pagination, setPagination] = useState<PaginationTable>({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const searchParams = useMemo(() => {
    const params: { search?: string; status?: string; is_active?: string } = {};

    if (contractSearch && contractSearch.trim()) {
      params.search = contractSearch;
    }

    if (contractFilters.status) {
      params.status = contractFilters.status;
    }

    if (contractFilters.is_active) {
      params.is_active = contractFilters.is_active;
    }

    return Object.keys(params).length > 0 ? params : undefined;
  }, [contractSearch, contractFilters]);

  const { data: contractsResponse, isLoading: loading } = useGetContracts(
    pagination,
    searchParams,
  );

  const contractData = useMemo(
    () => contractsResponse?.results || [],
    [contractsResponse],
  );

  /**
   * `{ ...prev, current: 1 }` siempre crea un objeto nuevo, asi que re-escribia
   * el estado incluso cuando ya estabamos en la pagina 1. Y `pagination` viaja
   * DENTRO del queryKey, asi que eso disparaba un refetch de red extra.
   * Aqui convergia solo porque estos callbacks ya estaban memoizados; quedaba
   * a un cambio de distancia del bucle de "Maximum update depth exceeded".
   */
  const resetToFirstPage = useCallback(() => {
    setPagination((prev) => (prev.current === 1 ? prev : { ...prev, current: 1 }));
  }, []);

  const handleContractSearch = useCallback(
    (v: string) => {
      setContractSearch(v);
      resetToFirstPage();
    },
    [resetToFirstPage],
  );

  const clearContractSearch = useCallback(() => {
    setContractSearch("");
    resetToFirstPage();
  }, [resetToFirstPage]);

  const handleFilterChange = useCallback(
    (filters: Filters) => {
      setContractFilters(filters);
      resetToFirstPage();
    },
    [resetToFirstPage],
  );
  return (
    <div id="contracts">
      <Row gutter={[16, 16]}>
        <Col
          xs={24}
          sm={12}
          md={24}
          lg={6}
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "flex-start",
          }}
        >
          <Title level={1} style={{ marginBottom: 5 }}>
            Contratos
          </Title>
        </Col>
      </Row>

      <div style={HEADER_ROW_STYLE}>
        <div className="overflow-hidden rounded-2xl border border-slate-200/70 bg-white/90">
          <ControlsRow
            isLg={!!screen.lg}
            searchLabel="contrato(s)"
            onSearch={handleContractSearch}
            onClear={clearContractSearch}
            className="gap-4 p-4 pb-3"
            rightButton={
              <Button
                onClick={() => setShowCreateContractModal(true)}
                size="lg"
                className="w-full text-white md:max-w-[250px] bg-[#0060ff] hover:bg-[#0052d4]"
              >
                Crear Contrato
              </Button>
            }
            filters={
              <ContractFilters
                onFilterChange={handleFilterChange}
                filters={contractFilters}
              />
            }
          />
          <div className="border-t border-slate-200/70 bg-white/95 px-2 pb-4 pt-3">
            <ContractsTable
              data={contractData}
              pagination={{
                ...pagination,
                total: contractsResponse?.count || 0,
              }}
              setPagination={setPagination}
              loading={loading}
              screen={screen}
            />
          </div>
        </div>
      </div>

      <CreateLeaseContractModal
        isOpen={showCreateContractModal}
        onOpenChange={setShowCreateContractModal}
        onSuccess={() => {
          setShowCreateContractModal(false);
        }}
      />
    </div>
  );
};

export default ContractsDashboard;
